/// <amd-module name="Test/ManageTestPlans/Actions/NewTestPlanPageActionsCreator" />
import { ClassificationNodeTypeConstants, INewTestPlanFields, INode, INodeStructureType } from "Test/ManageTestPlans/NewTestPlanPageContracts";
import { TestPlan, TestPlanCreateParams } from "TestPlanCommon/Contracts/TestPlan";
import { ITestPlanRestClient } from "TestPlanCommon/RestClient/TestPlan";
import { IProjectData, ITfsPageService } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";
import { routeUrl } from "VSS/Platform/Location";
import { TestPlanRouteParameters } from "Test/ManageTestPlansCommon/Constants";
import { TreeNodeStructureType, WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking.Types";
import { NewTestPlanPageActionsHub } from "./NewTestPlanPageActionsHub";


import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";

export class NewTestPlanPageActionsCreator {
    public static getInstance(): NewTestPlanPageActionsCreator {
        if (!NewTestPlanPageActionsCreator._instance) {
            NewTestPlanPageActionsCreator._instance = new NewTestPlanPageActionsCreator(NewTestPlanPageActionsHub.getInstance());
        }
        return NewTestPlanPageActionsCreator._instance;
    }

    private readonly depth: number = 15;
    private _actionsHub: NewTestPlanPageActionsHub;
    private _pageContext: IVssPageContext;
    private static _instance: NewTestPlanPageActionsCreator;
    constructor(actionsHub: NewTestPlanPageActionsHub) {
        this._actionsHub = actionsHub;
    }

    public initialize(context: IVssPageContext): void {
        this._pageContext = context;
        this.callGetNewTestPlanFieldsApi().then((fields: INewTestPlanFields) => {
            this._actionsHub.InitializeTestPlanFields.invoke(fields);
        });
    }

    public createTestPlan(name: string, projectId: string, areaPath: string, iteration: string): Promise<number | undefined> {
        this._actionsHub.beginCreateTestPlan.invoke();

        return new Promise((resolve, reject) => {
            this.callCreateTestPlanApi(name, projectId, areaPath, iteration).then(
                (testPlan: TestPlan) => {
                    this._actionsHub.createTestPlanSucceeded.invoke();
                    resolve(undefined);
                },
                (error: Error) => {
                    this._actionsHub.createTestPlanFailed.invoke(error);
                }
            );
        });
    }

    private callCreateTestPlanApi(name: string, projectId: string, areaPath: string, iteration: string): Promise<TestPlan | undefined> {
        const plan = {
            name: name,
            areaPath: areaPath,
            iteration: iteration
        } as TestPlanCreateParams;

        const testPlanClient = this._pageContext.getRestClient<ITestPlanRestClient>("TestHubRefresh-WebPlatform.ITestPlanRestClient");
        const testPlansPromise = testPlanClient.createTestPlan(plan, projectId);
        return new Promise((resolve, reject) => {
            testPlansPromise.then(
                (testPlan: TestPlan) => {
                    const testHubRoute: string = "ms.vss-test-web.testplans-hub-refresh-route";
                    const tfsPageData = this._pageContext.getService<ITfsPageService>("ITfsPageService").getData();

                    const routeValues: { [key: string]: string } = {};
                    routeValues["project"] = tfsPageData && tfsPageData.project ? tfsPageData.project.name : "";
                    routeValues["planId"] = String(testPlan.id);
                    routeValues["pivots"] = TestPlanRouteParameters.DefineTabRouteValue;
                    const url = routeUrl(this._pageContext, testHubRoute, routeValues);
                    const openedWindow = window.open(url, "_self");
                    if (openedWindow) {
                        openedWindow.opener = null;
                    }
                    resolve(undefined);
                },
                (error: Error) => {
                    reject(error);
                }
            );
        });
    }

    private callGetNewTestPlanFieldsApi(): Promise<INewTestPlanFields> {
        const project = this._getProjectFromContext();
        let projectId = "";
        let projectName = "";
        if (project) {
            projectId = project.id;
            projectName = project.name;
        }
        const topLevelProjectNode: INode = {
            id: parseInt(projectId),
            name: projectName,
            guid: undefined,
            structure: 0,
            type: ClassificationNodeTypeConstants.ProjectType,
            children: []
        };
        const witClient = this._pageContext.getRestClient<IWorkItemTrackingRestClient>("IWorkItemTrackingRestClient");
        const workItemsPromise = witClient.getRootNodes(projectName, this.depth);

        return new Promise<INewTestPlanFields>((resolve, reject) => {
            workItemsPromise.then(
                (nodes: WorkItemClassificationNode[]) => {
                    for (const node of nodes) {
                        topLevelProjectNode.children.push(this._mapClassificationNodesToLegacyNode(node));
                    }
                    this._link(topLevelProjectNode);

                    let areaPath: INode | null, iterationPath: INode | null;

                    this.getMruFieldsForCreateTestPlan().then((classificationFieldsMru: IClassificationFieldsMru)=>{
                        areaPath = this._findChildByStructure(topLevelProjectNode, 1);
                        iterationPath = this._findChildByStructure(topLevelProjectNode, 2);
                        
                        const fields: INewTestPlanFields = {
                            areaPath: areaPath,
                            iteration: iterationPath,
                            projectId: projectId,
                            areaPathMruIds: classificationFieldsMru.areaPathMru ? classificationFieldsMru.areaPathMru : [],
                            iterationMruIds: classificationFieldsMru.iterationPathMru ? classificationFieldsMru.iterationPathMru : []
                        }
                        resolve(fields);
                    });
                },
                (error: Error) => {
                    reject(error);
                }
            );
        });
    }


    private async getMruFieldsForCreateTestPlan(): Promise<IClassificationFieldsMru> {
        const projectFromContext: IProjectData|undefined = this._getProjectFromContext();
        const projectId: string = projectFromContext ? projectFromContext.id : "";

        const settingsService = this._pageContext.getService<ISettingsService>("ISettingsService");

        const [settingValues] = await Promise.all([
            settingsService.getEntriesAsync(
                ClassificationFieldsMruSettingKeys.SettingsKey,
                SettingsUserScope.Me,
                "project",
                projectId)
        ]);

        const mruValues: IClassificationFieldsMru = {
            projectId: projectId,
            areaPathMru: (settingValues && settingValues[ClassificationFieldsMruSettingKeys.AreaPathMruKey]) || [],
            iterationPathMru: (settingValues && settingValues[ClassificationFieldsMruSettingKeys.IterationPathMruKey]) || []
        }

        return mruValues;
    
    }

    /**
     * Get project from context
     */
    private _getProjectFromContext(): IProjectData | undefined {
        const tfsPageData = this._pageContext.getService<ITfsPageService>("ITfsPageService").getData();
        if (tfsPageData && tfsPageData.project) {
            const project = tfsPageData.project;
            return project;
        } else {
            return undefined;
        }
    }

    private _findChildByStructure(node: INode, structure: number): INode {
        if (node) {
            const children = node.children;
            if (children) {
                for (const child of children) {
                    if (child.structure === structure) {
                        return child;
                    }
                }
            }
        }
        return {} as INode;
    }

    private _mapClassificationNodesToLegacyNode(node: WorkItemClassificationNode): INode {
        const legacyNode: INode = {
            id: node.id,
            structure: INodeStructureType.Area,
            startDate: node.attributes ? new Date(node.attributes["startDate"]) : undefined,
            children: [],
            finishDate: node.attributes ? new Date(node.attributes["finishDate"]) : undefined,
            guid: node.identifier,
            name: node.name,
            parent: {} as INode,
            type: ClassificationNodeTypeConstants.AreaType
        };

        if (node.structureType === TreeNodeStructureType.Area) {
            legacyNode.structure = INodeStructureType.Area;
            legacyNode.type = ClassificationNodeTypeConstants.AreaType;
        } else if (node.structureType === TreeNodeStructureType.Iteration) {
            legacyNode.structure = INodeStructureType.Iteration;
            legacyNode.type = ClassificationNodeTypeConstants.IterationType;
        }

        if (node.children) {
            for (const childNode of node.children) {
                legacyNode.children.push(this._mapClassificationNodesToLegacyNode(childNode));
            }
        }

        return legacyNode;
    }

    private _link(parentNode: INode): INode {
        if (parentNode.children) {
            for (const node of parentNode.children) {
                node.parent = parentNode;
                this._link(node);
            }
        }

        return parentNode;
    }
}

export interface IClassificationFieldsMru {
    projectId: string;
    areaPathMru: number[];
    iterationPathMru: number[];
}

namespace ClassificationFieldsMruSettingKeys {
    export const SettingsKey = "ClassificationFieldsMru";
    export const AreaPathMruKey = "AreaPath";
    export const IterationPathMruKey = "IterationPath";
    export const MruLength = 5;
}
